import { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { importVariants, validateVariantsImport } from "../api/variantDimensions";

const HEADERS = {
  label: ["varyant anahtari", "varyant anahtarı", "anahtar"],
  option: ["varyant secenekleri", "varyant seçenekleri", "secenek", "seçenek", "secenekler", "seçenekler"],
  unit: ["birim"],
};

function normalizeHeaderKey(x) {
  return String(x || "")
    .trim()
    .toLocaleLowerCase("tr")
    .replace(/ı/g, "i")
    .replace(/ş/g, "s")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c")
    .replace(/\s+/g, " ");
}

function findHeaderIndex(headers, aliases) {
  const norm = headers.map(normalizeHeaderKey);
  for (const a of aliases) {
    const idx = norm.indexOf(normalizeHeaderKey(a));
    if (idx >= 0) return idx;
  }
  return -1;
}

export default function VariantsImportModal({ onClose, onImported }) {
  const [fileName, setFileName] = useState("");
  const [parseError, setParseError] = useState("");
  const [rows, setRows] = useState([]);

  const [validating, setValidating] = useState(false);
  const [validated, setValidated] = useState(null);

  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [result, setResult] = useState(null);

  const errorCount = useMemo(() => (validated?.errors || []).length, [validated]);
  const groupCount = useMemo(() => (validated?.groups || []).length, [validated]);
  const canSubmit = !!rows.length && !validating && !saving && errorCount === 0 && !!validated;

  async function handlePickFile(file) {
    setParseError("");
    setSubmitError("");
    setResult(null);
    setRows([]);
    setValidated(null);
    setFileName(file?.name || "");
    if (!file) return;

    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array", cellDates: true });
      const sheetName = wb.SheetNames?.[0];
      if (!sheetName) throw new Error("Excel sayfası bulunamadı");
      const ws = wb.Sheets[sheetName];

      const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
      if (!Array.isArray(aoa) || aoa.length < 2) {
        throw new Error("Excel boş veya başlık satırı eksik");
      }

      const headerRow = (aoa[0] || []).map((x) => String(x || "").trim());
      const idx = {
        label: findHeaderIndex(headerRow, HEADERS.label),
        option: findHeaderIndex(headerRow, HEADERS.option),
        unit: findHeaderIndex(headerRow, HEADERS.unit),
      };

      const missing = [];
      if (idx.label < 0) missing.push("Varyant Anahtarı");
      if (idx.option < 0) missing.push("Varyant Seçenekleri");
      if (idx.unit < 0) missing.push("Birim");
      if (missing.length) {
        throw new Error(`Eksik zorunlu sütun(lar): ${missing.join(", ")}`);
      }

      const parsed = [];
      for (let i = 1; i < aoa.length; i += 1) {
        const r = aoa[i] || [];
        const label = String(r[idx.label] || "").trim();
        const option = String(r[idx.option] || "").trim();
        const unit = String(r[idx.unit] || "").trim();
        if (!label && !option && !unit) continue;
        parsed.push({ label, option, unit });
      }

      if (!parsed.length) {
        setParseError("Dosyada veri satırı bulunamadı.");
        return;
      }

      setRows(parsed);

      setValidating(true);
      try {
        const res = await validateVariantsImport(parsed);
        setValidated(res);
      } catch (err) {
        console.error("VARIANTS VALIDATE ERROR", err);
        setParseError(err?.response?.data?.error?.message || err?.message || "Doğrulama başarısız");
      } finally {
        setValidating(false);
      }
    } catch (err) {
      console.error("EXCEL PARSE ERROR", err);
      setParseError(err?.message || "Excel okunamadı");
    }
  }

  async function submit() {
    setSubmitError("");
    setResult(null);
    if (!rows.length) return;
    if (errorCount > 0) {
      setSubmitError("Hatalı satırlar var. Lütfen düzeltin.");
      return;
    }

    setSaving(true);
    try {
      const res = await importVariants(rows);
      setResult(res);
      await onImported?.(res);
    } catch (err) {
      console.error("VARIANTS IMPORT ERROR", err);
      const status = err?.response?.status;
      const dataObj = err?.response?.data;
      const message =
        dataObj?.error?.message ||
        dataObj?.message ||
        (typeof dataObj === "string" ? dataObj : null) ||
        err?.message ||
        null;
      setSubmitError(`İçe aktarılamadı${status ? ` (HTTP ${status})` : ""}${message ? `: ${message}` : ""}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          Varyant Excel İçe Aktar
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Sütunlar: <b>Varyant Anahtarı</b>, <b>Varyant Seçenekleri</b>, <b>Birim</b>. Aynı anahtarın farklı seçenekleri için
          ayrı satır kullanın. Seçenek boşsa serbest giriş (sayı/metin), doluysa dropdown olur.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {parseError ? <Alert severity="error">{parseError}</Alert> : null}
          {submitError ? <Alert severity="error">{submitError}</Alert> : null}

          {result ? (
            <Alert severity={result?.errors?.length ? "warning" : "success"}>
              Yeni anahtar: <b>{result.dimensions_created || 0}</b>, güncellenen:{" "}
              <b>{result.dimensions_updated || 0}</b>
              <br />
              Yeni seçenek: <b>{result.options_inserted || 0}</b>, atlanan (zaten var):{" "}
              <b>{result.options_skipped || 0}</b>
              {result?.errors?.length ? (
                <>
                  <br />
                  Hata: <b>{result.errors.length}</b>
                  <ul style={{ margin: "4px 0 0 16px" }}>
                    {result.errors.map((e, i) => (
                      <li key={i}>
                        <b>{e.label}</b>: {e.message}
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
            </Alert>
          ) : null}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
            <Button variant="outlined" component="label" disabled={saving}>
              Dosya Seç (.xlsx)
              <input
                type="file"
                hidden
                accept=".xlsx,.xls,.csv"
                onChange={(e) => handlePickFile(e.target.files?.[0] || null)}
              />
            </Button>
            <Typography variant="body2" color="text.secondary">
              {fileName ? `Seçili: ${fileName}` : "Henüz dosya seçilmedi"}
              {validating ? " (doğrulanıyor...)" : ""}
            </Typography>
          </Stack>

          {validated?.groups?.length ? (
            <Box sx={{ overflowX: "auto" }}>
              <Typography variant="body2" color={errorCount > 0 ? "error" : "text.secondary"}>
                Anahtar: <b>{groupCount}</b> — Satır: <b>{rows.length}</b> — Hata: <b>{errorCount}</b>
              </Typography>
              <Table size="small" sx={{ mt: 1 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Anahtar (Label)</TableCell>
                    <TableCell>Otomatik Key</TableCell>
                    <TableCell>Tip</TableCell>
                    <TableCell>Birim</TableCell>
                    <TableCell>Seçenekler</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(validated.groups || []).map((g, i) => (
                    <TableRow key={i} hover>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>
                        <b>{g.label}</b>
                      </TableCell>
                      <TableCell>
                        <code>{g.key}</code>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={g.value_type} variant="outlined" />
                      </TableCell>
                      <TableCell>{g.unit || "—"}</TableCell>
                      <TableCell sx={{ maxWidth: 360 }}>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {(g.options || []).map((o, oi) => (
                            <Chip key={oi} size="small" label={o} variant="outlined" />
                          ))}
                          {!(g.options || []).length ? (
                            <Typography variant="caption" color="text.secondary">
                              (serbest giriş)
                            </Typography>
                          ) : null}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>
          Kapat
        </Button>
        <Button onClick={submit} variant="contained" disabled={!canSubmit}>
          {saving ? "Oluşturuluyor..." : "Oluştur"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
